/**
 * Image Lightbox Component
 * A simple, accessible lightbox for viewing images in full screen
 */

import { lockBodyScroll, unlockBodyScroll } from '../modules/scroll-lock.js';

const ImageLightbox = (function() {
  let lightboxElement = null;
  let imageElement = null;
  let closeButton = null;
  let isOpen = false;

  /**
   * Create the lightbox DOM elements
   */
  function createLightbox() {
    if (lightboxElement) return;

    // Create backdrop
    lightboxElement = document.createElement('div');
    lightboxElement.id = 'image-lightbox';
    lightboxElement.setAttribute('data-img-error-own', '');
    lightboxElement.className = 'fixed inset-0 z-50 hidden items-center justify-center bg-black/90 backdrop-blur-sm';
    lightboxElement.setAttribute('role', 'dialog');
    lightboxElement.setAttribute('aria-modal', 'true');
    lightboxElement.setAttribute('aria-label', 'Image viewer');

    // Create close button
    closeButton = document.createElement('button');
    closeButton.className = 'absolute top-4 right-4 z-10 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all duration-200 backdrop-blur-sm border border-white/20';
    closeButton.setAttribute('aria-label', 'Close image viewer');
    closeButton.innerHTML = '<i class="fa fa-times text-xl"></i>';

    // Create image container
    const imageContainer = document.createElement('div');
    imageContainer.className = 'relative max-w-[90vw] max-h-[90vh] flex items-center justify-center';

    // Create image
    imageElement = document.createElement('img');
    imageElement.className = 'max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl select-none';
    imageElement.setAttribute('alt', 'Full size image');

    // Create loading spinner
    const spinner = document.createElement('div');
    spinner.id = 'lightbox-spinner';
    spinner.className = 'absolute inset-0 flex items-center justify-center';
    spinner.innerHTML = '<i class="fa fa-spinner fa-spin text-white text-3xl"></i>';

    // Assemble
    imageContainer.appendChild(spinner);
    imageContainer.appendChild(imageElement);
    lightboxElement.appendChild(closeButton);
    lightboxElement.appendChild(imageContainer);

    document.body.appendChild(lightboxElement);

    // Bind events
    bindEvents();
  }

  /**
   * Bind event listeners
   */
  function bindEvents() {
    // Close on backdrop click
    lightboxElement.addEventListener('click', function(e) {
      if (e.target === lightboxElement) {
        close();
      }
    });

    // Close button
    closeButton.addEventListener('click', close);

    // Keyboard navigation
    document.addEventListener('keydown', function(e) {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        close();
      }
    });

    // Hide spinner when image loads
    imageElement.addEventListener('load', function() {
      const spinner = document.getElementById('lightbox-spinner');
      if (spinner) {
        spinner.classList.add('hidden');
      }
      // Ensure image is visible
      imageElement.style.opacity = '1';
    });

    // Handle image load errors
    imageElement.addEventListener('error', function() {
      const spinner = document.getElementById('lightbox-spinner');
      if (spinner) {
        spinner.classList.remove('hidden');
        spinner.innerHTML = '<div class="text-white text-center"><i class="fa fa-exclamation-triangle text-3xl mb-2"></i><p>' + window.tOr('common.image_load_failed', 'Failed to load image') + '</p></div>';
      }
      imageElement.style.display = 'none';
    });
  }

  /**
   * Open lightbox with specified image
   * @param {string} src - Image source URL
   * @param {string} [alt] - Alt text for the image
   */
  function open(src, alt) {
    createLightbox();

    // Show spinner, reset image state
    const spinner = document.getElementById('lightbox-spinner');
    if (spinner) {
      spinner.classList.remove('hidden');
      spinner.innerHTML = '<i class="fa fa-spinner fa-spin text-white text-3xl"></i>';
    }
    imageElement.style.display = '';

    // Set image source
    imageElement.src = src;
    if (alt) {
      imageElement.setAttribute('alt', alt);
    }

    // Show lightbox
    lightboxElement.classList.remove('hidden');
    lightboxElement.classList.add('flex');
    isOpen = true;

    // Prevent body scroll
    lockBodyScroll();

    // Focus the close button for accessibility
    closeButton.focus();
  }

  /**
   * Close the lightbox
   */
  function close() {
    if (!lightboxElement || !isOpen) return;

    lightboxElement.classList.add('hidden');
    lightboxElement.classList.remove('flex');
    isOpen = false;

    // Restore body scroll
    unlockBodyScroll();

    // Clear image source
    imageElement.src = '';
  }

  /**
   * Initialize lightbox on images with data-lightbox attribute
   */
  function init() {
    // Use event delegation for dynamically added images
    document.addEventListener('click', function(e) {
      const trigger = e.target.closest('[data-lightbox]');
      if (trigger) {
        e.preventDefault();
        const src = trigger.getAttribute('data-lightbox') || trigger.getAttribute('href') || trigger.src;
        const alt = trigger.getAttribute('alt') || trigger.getAttribute('title') || 'Image';
        open(src, alt);
      }
    });
  }

  // Auto-initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Public API
  return {
    open: open,
    close: close,
    init: init
  };
})();

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ImageLightbox;
}

// Make available globally
window.ImageLightbox = ImageLightbox;
